import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import mainSquare from "../../logo.svg";
import styles from "./index.module.scss";
import { useMediaQuery } from "react-responsive";
import axios from "axios";
import ThemeSwitcher from "../../components/ThemeSwitcher";

const pageList = ["Facebook 粉絲專頁"];
const petTopics = ["幼犬訓練", "貓咪飲食", "醫療經驗", "行為改善", "寵物旅遊"];
const petTypeOptions = [
  "狗狗",
  "貓咪",
  "兔兔",
  "鳥類",
  "鼠類",
  "爬蟲",
  "水族",
  "其他寵物",
];

let serverConnection;
const host = window.location.origin.includes("localhost")
  ? "https://localhost:8443"
  : "https://api.puipi.com";

const buildRoomSearch = (selfPetType, peerPetType) => {
  const roomSearch = new URLSearchParams();

  if (selfPetType) {
    roomSearch.set("selfPetType", selfPetType);
  }
  if (peerPetType) {
    roomSearch.set("peerPetType", peerPetType);
  }

  const queryString = roomSearch.toString();
  return queryString ? `?${queryString}` : "";
};

const LandingPage = () => {
  const history = useHistory();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 905px)" });
  const [isShowNavList, setIsShowNavList] = useState(false);
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [currentUserCount, setCurrentUserCount] = useState(0);
  const [petType, setPetType] = useState(petTypeOptions[0]);

  const handleMenuClick = () => {
    window.open("https://www.facebook.com/people/PuiPi-Chat/100090050661123/");
  };

  useEffect(() => {
    let isMounted = true;
    const fetchCurrentUserCount = () => {
      axios
        .get(`${host}/users`)
        .then(({ data }) => {
          const rawCount = Number(data?.result ?? data?.count ?? data?.users ?? 0);
          if (isMounted) {
            setCurrentUserCount(Number.isNaN(rawCount) ? 0 : rawCount);
          }
        })
        .catch(() => {
          if (isMounted) {
            setCurrentUserCount(0);
          }
        });
    };

    fetchCurrentUserCount();
    const timer = setInterval(fetchCurrentUserCount, 12000);

    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!username) {
      return undefined;
    }

    const pathPrefix = window.location.pathname === "/" ? "" : window.location.pathname;
    const urlPath = `${pathPrefix}/chat/pool?petType=${encodeURIComponent(
      petType
    )}`;
    serverConnection = new WebSocket(
      window.location.origin.includes("localhost")
        ? `wss://localhost:8443${urlPath}`
        : `wss://api.puipi.com${urlPath}`,
      [username]
    );
    serverConnection.onopen = () => {
      console.log("Websocket connected [pool]");
    };
    serverConnection.onmessage = ({ data }) => {
      const { roomId, selfPetType, peerPetType } = JSON.parse(data);
      serverConnection.close();
      history.push(`/chat/${roomId}${buildRoomSearch(selfPetType, peerPetType)}`);
    };

    return () => {
      if (
        serverConnection &&
        (serverConnection.readyState === 0 || serverConnection.readyState === 1)
      ) {
        serverConnection.close();
      }
    };
  }, [history, petType, username]);

  const handleConfirm = () => {
    setStep(2);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    setUsername(Math.random().toString(36).slice(2));
  };

  const handleVideoConfirm = () => {
    history.push(`/video-chat/pool?petType=${encodeURIComponent(petType)}`);
  };

  const handleCancelMatching = () => {
    if (
      serverConnection &&
      (serverConnection.readyState === 0 || serverConnection.readyState === 1)
    ) {
      serverConnection.close();
    }
    setStep(1);
    setUsername("");
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerBrand}>
          <img className={styles.mainSquareImage} src={mainSquare} alt="PuiPi Pet Chat Logo" />
          <div>
            <div className={styles.headerTitle}>PuiPi Pet Chat</div>
            <div className={styles.headerSubtitle}>寵物主人的即時聊天平台</div>
          </div>
        </div>
        <div className={styles.headerRight}>
          <ThemeSwitcher />
          {!isTabletOrMobile ? (
            <ul className={styles.pageList}>
              {pageList.map((item) => (
                <li onClick={handleMenuClick} key={item}>
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <button
              type="button"
              onClick={() => setIsShowNavList(!isShowNavList)}
              aria-label={isShowNavList ? "關閉選單" : "開啟選單"}
              aria-expanded={isShowNavList}
              aria-controls="landing-mobile-nav"
              className={styles.listButton}
            >
              ☰
            </button>
          )}
        </div>
      </header>

      {isShowNavList && (
        <div className={styles.navList} id="landing-mobile-nav">
          {pageList.map((item) => (
            <div onClick={handleMenuClick} key={item}>
              {item}
            </div>
          ))}
          <button
            type="button"
            aria-label="關閉選單"
            onClick={() => setIsShowNavList(!isShowNavList)}
            className={styles.closeList}
          />
        </div>
      )}

      <main className={styles.mainContent}>
        <section className={styles.heroCard}>
          <div className={styles.heroBadge}>專屬寵物主人的匿名聊天室</div>
          <h1 className={styles.heroTitle}>和懂寵物的人，一起聊照護、日常與療癒</h1>
          <p className={styles.mainIntroText}>
            文字與視訊兩種模式，隨時配對同樣熱愛毛孩、寵物的夥伴，交流經驗不孤單。
          </p>
          <div className={styles.statRow}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>目前在線寵主</span>
              <span className={styles.statValue}>{currentUserCount} 人</span>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>平台狀態</span>
              <span className={styles.statValue}>正常運作中</span>
            </div>
          </div>
          <div className={styles.preferenceBlock}>
            <label htmlFor="landing-pet-type" className={styles.preferenceLabel}>
              你的寵物類型
            </label>
            <select
              id="landing-pet-type"
              className={styles.preferenceSelect}
              value={petType}
              onChange={(e) => setPetType(e.target.value)}
            >
              {petTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <div className={styles.preferenceHint}>僅作為聊天開場，不影響隨機配對結果</div>
          </div>
          <div className={styles.joinWaitListButtonContainer}>
            {step === 1 && (
              <div className={styles.ctaGroup}>
                <button onClick={handleConfirm} className={styles.joinWaitListButton}>
                  開始文字聊天
                </button>
                <button
                  onClick={handleVideoConfirm}
                  className={`${styles.joinWaitListButton} ${styles.secondaryBtn}`}
                >
                  開始視訊聊天
                </button>
              </div>
            )}
            {step === 2 && (
              <div className={styles.waitingCard}>
                <div role="status" aria-live="polite" className={styles.waitingText}>
                  正在為你隨機配對其他寵物主人...
                </div>
                <button
                  type="button"
                  className={styles.leave_btn}
                  onClick={handleCancelMatching}
                >
                  取消等待
                </button>
              </div>
            )}
          </div>
        </section>

        <section className={styles.featureGrid}>
          <article className={styles.featureCard}>
            <h3>安全匿名</h3>
            <p>匿名聊天，專注討論毛孩、寵物照護與日常互動。</p>
          </article>
          <article className={styles.featureCard}>
            <h3>即時配對</h3>
            <p>一鍵進入文字聊天，快速找到同頻率的飼主、寵物主人夥伴。</p>
          </article>
          <article className={styles.featureCard}>
            <h3>經驗交流</h3>
            <p>從飲食、醫療到訓練，累積最實用的養寵心得。</p>
          </article>
        </section>

        <section className={styles.topicSection}>
          <h2>熱門聊天主題</h2>
          <div className={styles.topicList}>
            {petTopics.map((topic) => (
              <span className={styles.topicPill} key={topic}>
                #{topic}
              </span>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default LandingPage;
